import React from 'react';
import { FileText } from 'lucide-react';
import { FormField } from '../../types/formBuilder';
import {
  TextInput,
  TextareaInput,
  NumberInput,
  RangeInput,
  SelectInput,
  BooleanInput,
  FileInput,
  DropdownWithImageInput,
  PromptBuilderInput,
  DateInput,
  TimeInput,
} from './inputs';

interface DynamicFormFieldsProps {
  fields: FormField[];
  values: Record<string, any>;
  onChange: (fieldId: string, value: any) => void;
  errors?: Record<string, string>;
  isAdvancedMode?: boolean;
  disabled?: boolean;
  onAnyInputFocusChange?: (isFocused: boolean) => void;
  /** Page / Pro Form / ProMax app id for uploads (Page Settings). */
  appId?: string;
}

const FormFieldRenderer: React.FC<{
  field: FormField;
  value: any;
  onChange: (value: any) => void;
  error?: string;
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  appId?: string;
}> = ({ field, value, onChange, error, onFocus, onBlur, disabled = false, appId }) => {
  switch (field.type) {
    case 'text':
    case 'email':
    case 'password':
    case 'url':
    case 'tel':
      return (
        <TextInput
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
        />
      );

    case 'date':
      return (
        <DateInput
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
        />
      );

    case 'time':
      return (
        <TimeInput
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
        />
      );

    case 'textarea':
      return (
        <TextareaInput
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
        />
      );

    case 'number':
      return (
        <NumberInput
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
        />
      );

    case 'range':
      return (
        <RangeInput
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case 'select':
      return (
        <SelectInput
          field={field}
          value={value}
          onChange={onChange}
          error={error}
          onFocus={onFocus}
          onBlur={onBlur}
          disabled={disabled}
        />
      );

    case 'boolean':
      return (
        <BooleanInput
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case 'file':
      return (
        <FileInput
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          appId={appId}
        />
      );

    case 'dropdown_with_image':
      return (
        <DropdownWithImageInput
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
        />
      );

    case 'prompt_builder':
      return (
        <PromptBuilderInput
          field={field}
          value={value}
          onChange={onChange}
          disabled={disabled}
          appId={appId}
        />
      );

    default:
      return (
        <div className="p-4 border border-subtle rounded-xl bg-elevated">
          <div className="flex items-center space-x-2 text-th-secondary">
            <FileText className="w-4 h-4" />
            <span>Unsupported field type: {field.type}</span>
          </div>
        </div>
      );
  }
};

export const DynamicFormFields: React.FC<DynamicFormFieldsProps> = ({
  fields,
  values,
  onChange,
  errors = {},
  isAdvancedMode,
  disabled,
  onAnyInputFocusChange,
  appId,
}) => {
  const focusedFieldsRef = React.useRef<Set<string>>(new Set());

  // Helper function to generate consistent field keys
  const getFieldKey = (field: FormField, index: number): string => {
    return field.key || field.variable_name || field.id || `field_${index}`;
  };

  const handleFieldFocus = React.useCallback((fieldKey: string) => {
    focusedFieldsRef.current.add(fieldKey);
    if (onAnyInputFocusChange && focusedFieldsRef.current.size === 1) {
      onAnyInputFocusChange(true);
    }
  }, [onAnyInputFocusChange]);

  const handleFieldBlur = React.useCallback((fieldKey: string) => {
    // Use setTimeout to allow focus to shift between fields
    setTimeout(() => {
      focusedFieldsRef.current.delete(fieldKey);
      if (onAnyInputFocusChange && focusedFieldsRef.current.size === 0) {
        onAnyInputFocusChange(false);
      }
    }, 100);
  }, [onAnyInputFocusChange]);

  // Maintain original order from input schema - filter but preserve order
  const visibleFields = fields.filter(field => {
    // Hide hidden fields from the UI
    if (field.hidden) return false;
    
    // Show advanced fields only in advanced mode
    if (field.advanced && !isAdvancedMode) return false;
    
    return true;
  });

  if (visibleFields.length === 0) {
    return (
      <div className="text-center py-8 text-th-secondary">
        <FileText className="w-12 h-12 mx-auto mb-4" />
        <p>No fields available to display</p>
        {!isAdvancedMode && fields.some(f => f.advanced) && (
          <p className="text-sm mt-2">Enable advanced mode to see more options</p>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {visibleFields.map((field, index) => {
        const fieldKey = getFieldKey(field, index);
        
        return (
          <div key={`${fieldKey}-${index}`} className="space-y-2">
            {/* Field Label */}
            <label className="block text-sm font-medium text-th-primary">
              {field.label}
              {field.required && <span className="text-status-error ml-1">*</span>}
              {field.advanced && (
                <span className="ml-2 text-xs text-status-warning bg-status-warning/20 px-2 py-0.5 rounded">
                  Advanced
                </span>
              )}
            </label>

            {/* Field Description */}
            {field.description && (
              <p className="text-sm text-th-secondary">{field.description}</p>
            )}

            {/* Field Renderer */}
            <FormFieldRenderer
              field={field}
              value={values[fieldKey]}
              onChange={(value) => onChange(fieldKey, value)}
              error={errors[field.id]}
              onFocus={() => handleFieldFocus(fieldKey)}
              onBlur={() => handleFieldBlur(fieldKey)}
              disabled={disabled}
              appId={appId}
            />

            {/* Error Message */}
            {errors[field.id] && (
              <p className="text-sm text-status-error">{errors[field.id]}</p>
            )}
          </div>
        );
      })}
    </div>
  );
};